/**
 * The one drawer.
 *
 * Two screens carried their own copy of the scrim, the panel, the Escape
 * listener and the close button, and neither moved focus into the panel when
 * it opened — which the asset drawer's own comment acknowledged. Radix Dialog
 * does the parts a hand-rolled drawer gets wrong: focus is trapped inside,
 * Escape and the scrim close it, and focus returns to whatever opened it.
 *
 * The visual is unchanged: a panel from the right, the iOS sheet curve, the
 * scrim fading slightly faster than the panel travels.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type React from 'react';

import { Button } from './Button.tsx';

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  actions,
  width = 'md',
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** The heading, and the accessible name. */
  title: React.ReactNode;
  /** One line under the heading — what this is, not what to do. */
  subtitle?: React.ReactNode;
  /** Controls in the head, right of the title. */
  actions?: React.ReactNode;
  /** `md` fits a form; `lg` fits a detail drawer with tabs and tables. */
  width?: 'md' | 'lg';
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-scrim" />
        <Dialog.Content className={`drawer drawer-${width}`} aria-describedby={undefined}>
          <div className="drawer-head">
            <div>
              <Dialog.Title asChild>
                <h2 className="drawer-title">{title}</h2>
              </Dialog.Title>
              {subtitle !== undefined && <div className="small muted">{subtitle}</div>}
            </div>
            {actions}
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm" iconOnly icon={<X size={16} />}>
                Close
              </Button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
